/**
 * useL1Field Hook
 *
 * Manages L1 (User-Only) encrypted field operations with WebAuthn PRF.
 *
 * FERP-PII-052: User-controlled encryption for critical financial data.
 *
 * Security Model:
 * - L1 fields (SSN, bank accounts) require user's WebAuthn PRF for access
 * - PRF output is passed to backend (never stored client-side)
 * - Each field gets unique DEK derived from PRF via HKDF
 * - NO ONE else can access L1 data - not admins, not guardians, not break-glass
 *
 * @example
 * ```tsx
 * const {
 *   storeField,
 *   getField,
 *   hasField,
 *   deleteField,
 *   loading,
 *   error,
 * } = useL1Field();
 *
 * // Store SSN with biometric authentication
 * const result = await storeField('ssn', '123-45-6789');
 *
 * // Retrieve SSN (prompts for biometric)
 * const { value } = await getField('ssn');
 * ```
 */

import { useState, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- DATA-LAYER hook, allowed to use Apollo Client directly
import { useMutation, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { webAuthnRepository } from '@ferp/workflow-forms';

// ============================================================================
// GraphQL Operations
// ============================================================================

const STORE_L1_FIELD = gql`
  mutation StoreL1Field($input: StoreL1FieldInput!) {
    storeL1Field(input: $input) {
      success
      fieldName
      error
    }
  }
`;

const GET_L1_FIELD = gql`
  query GetL1Field($input: GetL1FieldInput!) {
    getL1Field(input: $input) {
      success
      fieldName
      value
      error
    }
  }
`;

const HAS_L1_FIELD = gql`
  query HasL1Field($fieldName: String!) {
    hasL1Field(fieldName: $fieldName)
  }
`;

const DELETE_L1_FIELD = gql`
  mutation DeleteL1Field($fieldName: String!) {
    deleteL1Field(fieldName: $fieldName)
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface StoreL1FieldInput {
  fieldName: string;
  value: string;
  prfOutput: string;
}

export interface GetL1FieldInput {
  fieldName: string;
  prfOutput: string;
}

export interface StoreL1FieldResult {
  success: boolean;
  fieldName: string;
  error?: string;
}

export interface GetL1FieldResult {
  success: boolean;
  fieldName: string;
  value?: string;
  error?: string;
}

export interface UseL1FieldReturn {
  /**
   * Store an L1 encrypted field
   *
   * Prompts user for biometric authentication, then encrypts and stores the value.
   *
   * @param fieldName - Field identifier (e.g., "ssn", "bank_account_number")
   * @param value - Plaintext value to encrypt
   * @returns Result with success status
   */
  storeField: (fieldName: string, value: string) => Promise<StoreL1FieldResult>;

  /**
   * Retrieve an L1 encrypted field
   *
   * Prompts user for biometric authentication, then decrypts and returns the value.
   *
   * @param fieldName - Field identifier to retrieve
   * @returns Result with decrypted value
   */
  getField: (fieldName: string) => Promise<GetL1FieldResult>;

  /**
   * Check if an L1 field exists (without decrypting)
   *
   * Does NOT require biometric authentication.
   *
   * @param fieldName - Field identifier to check
   * @returns Whether the field exists
   */
  hasField: (fieldName: string) => Promise<boolean>;

  /**
   * Delete an L1 field (GDPR right-to-erasure)
   *
   * Permanently removes the encrypted field.
   *
   * @param fieldName - Field identifier to delete
   * @returns Whether deletion succeeded
   */
  deleteField: (fieldName: string) => Promise<boolean>;

  /**
   * Authenticate with PRF without performing field operation
   *
   * Useful for pre-authenticating before batch operations.
   *
   * @returns PRF output if successful
   */
  authenticatePrf: () => Promise<string | null>;

  /** Current PRF output (valid for current session) */
  prfOutput: string | null;

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Clear error */
  clearError: () => void;

  /** Clear PRF output (for security timeout) */
  clearPrfOutput: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useL1Field(): UseL1FieldReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prfOutput, setPrfOutput] = useState<string | null>(null);

  // GraphQL mutations
  const [storeL1FieldMutation] = useMutation<Record<string, any>>(STORE_L1_FIELD);
  const [deleteL1FieldMutation] = useMutation<Record<string, any>>(DELETE_L1_FIELD);

  // GraphQL queries (lazy for on-demand execution)
  const [getL1FieldQuery] = useLazyQuery<Record<string, any>>(GET_L1_FIELD);
  const [hasL1FieldQuery] = useLazyQuery<Record<string, any>>(HAS_L1_FIELD);

  // Authenticate with PRF
  const authenticatePrf = useCallback(async (): Promise<string | null> => {
    setError(null);
    setLoading(true);

    try {
      const result = await webAuthnRepository.authenticateWithPrf();

      if (!result.isValid) {
        setError(result.error || 'PRF authentication failed');
        return null;
      }

      setPrfOutput(result.prfOutput);
      return result.prfOutput;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'PRF authentication failed';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Store L1 field
  const storeField = useCallback(
    async (fieldName: string, value: string): Promise<StoreL1FieldResult> => {
      setError(null);
      setLoading(true);

      try {
        // Get PRF output (prompt for biometric)
        let currentPrfOutput = prfOutput;
        if (!currentPrfOutput) {
          currentPrfOutput = await authenticatePrf();
          if (!currentPrfOutput) {
            return {
              success: false,
              fieldName,
              error: 'Biometric authentication required',
            };
          }
        }

        // Call backend mutation
        const { data } = await storeL1FieldMutation({
          variables: {
            input: {
              fieldName,
              value,
              prfOutput: currentPrfOutput,
            },
          },
        });

        return data?.storeL1Field ?? {
          success: false,
          fieldName,
          error: 'No response from server',
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to store field';
        setError(message);
        return {
          success: false,
          fieldName,
          error: message,
        };
      } finally {
        setLoading(false);
      }
    },
    [prfOutput, authenticatePrf, storeL1FieldMutation]
  );

  // Get L1 field
  const getField = useCallback(
    async (fieldName: string): Promise<GetL1FieldResult> => {
      setError(null);
      setLoading(true);

      try {
        // Get PRF output (prompt for biometric)
        let currentPrfOutput = prfOutput;
        if (!currentPrfOutput) {
          currentPrfOutput = await authenticatePrf();
          if (!currentPrfOutput) {
            return {
              success: false,
              fieldName,
              error: 'Biometric authentication required',
            };
          }
        }

        // Call backend query
        const { data } = await getL1FieldQuery({
          variables: {
            input: {
              fieldName,
              prfOutput: currentPrfOutput,
            },
          },
          // @ts-expect-error Known properties
          fetchPolicy: 'network-only', // Always fetch fresh (no cache for sensitive data)
        });

        return data?.getL1Field ?? {
          success: false,
          fieldName,
          error: 'No response from server',
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to retrieve field';
        setError(message);
        return {
          success: false,
          fieldName,
          error: message,
        };
      } finally {
        setLoading(false);
      }
    },
    [prfOutput, authenticatePrf, getL1FieldQuery]
  );

  // Check if field exists
  const hasField = useCallback(
    async (fieldName: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        const { data } = await hasL1FieldQuery({
          variables: { fieldName },
        });

        return data?.hasL1Field ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to check field';
        setError(message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [hasL1FieldQuery]
  );

  // Delete field
  const deleteField = useCallback(
    async (fieldName: string): Promise<boolean> => {
      setError(null);
      setLoading(true);

      try {
        const { data } = await deleteL1FieldMutation({
          variables: { fieldName },
        });

        return data?.deleteL1Field ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete field';
        setError(message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [deleteL1FieldMutation]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear PRF output (for security timeout)
  const clearPrfOutput = useCallback(() => {
    setPrfOutput(null);
  }, []);

  return {
    storeField,
    getField,
    hasField,
    deleteField,
    authenticatePrf,
    prfOutput,
    loading,
    error,
    clearError,
    clearPrfOutput,
  };
}
